import { useEffect, useState, useContext, useMemo, useTransition, useCallback } from 'react'
import { Delete, Security, Send, PersonOutlined } from '@mui/icons-material'
import { TableCell, TableRow, Checkbox, Tooltip, Fab, CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Users as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { selectIsUserAdmin } from '@authed/selectors'
import { CvsDrawer, CvsTable, Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { userContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { Role } from '@global/types'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { showSnackbar, getCellContent } from 'functions'
import { EditUser } from './edit-user'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import { Fuzzy } from '../../../utils/fuse-search'
import { useUserRowInteractions } from '../hooks/useUserRowInteractions'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const Users = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const ModalContext = useContext(modalContext)
  const isAdmin = useAppSelector(selectIsUserAdmin)
  const isManagerWithPermission = useAppSelector(
    state => state.Auth.user?.role === Role.MANAGER && state.Auth.permissions?.manage_users,
  )
  const { byId, fetchUsers, initializeUsers } = useContext(userContext)
  const [loaded, setLoaded] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [sortedUsers, setSortedUsers] = useState(null)
  const [lastClickedRowIdx, setLastClickedRowIdx] = useState(null)
  const [selected, setSelected] = useState({})
  const [visible, setVisible] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [isPending, startTransition] = useTransition()
  const columns = [
    { label: t`Name`, type: 'avatar', width: '25%', lookup: 'name' },
    { label: t`Email`, width: '40%', lookup: 'email' },
    { label: t`Role`, width: '15%', lookup: 'type' },
    { label: t`Last Login`, type: 'date', width: '20%', lookup: 'last_login' },
  ]

  const { handleSelectMultipleRows, shiftKey } = useUserRowInteractions({
    setSelected,
    selectedRows: selected,
    sortedUsers,
  })

  useEffect(() => {
    setVisible(selectedUser)
  }, [selectedUser])

  const users = useMemo(() => Object.keys(byId).map(id => byId[id]), [byId])

  const selectedUsers = useMemo(
    () =>
      Object.keys(selected)
        .filter(key => selected[key])
        .map(id => byId[id]),
    [selected],
  )

  const isResendEnable = selectedUsers.every(user => user.last_login == null)
  const resendTooltipText = isResendEnable
    ? t`Resend Activation Link`
    : t`Selected users must have their last login as Never to receive an activation link`

  const [displayUsers, setDisplayUsers] = useState(users)
  const filterChanged = search => {
    setSearchTerm(search)

    startTransition(() => {
      const results = performSearch(search)
      setDisplayUsers(results)
    })
  }

  const fuzzy = useMemo(() => new Fuzzy(users, { keys: ['name'] }), [users])

  const performSearch = useCallback(
    term => {
      if (!term || term.trim() === '') {
        return users
      }

      const results = fuzzy.search(term)

      return results
    },
    [fuzzy, users],
  )

  useEffect(() => {
    filterChanged(searchTerm)
  }, [users])

  const onClose = () => {
    setSelectedUser(null)
    setVisible(false)
  }

  useEffect(() => {
    setLoaded(false)
    fetchUsers().then(() => setLoaded(true))
  }, [fetchUsers])

  const onReset = async () => {
    await api.post('/api/v1/account/user/bulk/force-password-reset', {
      user_ids: Object.keys(selected),
    })
    dispatch(
      showSnackbar({
        message: t`Passwords Reset!`,
        type: 'SUCCESS',
        duration: 5000,
      }),
    )
  }

  const onResend = async () => {
    if (isResendEnable) {
      try {
        await api.post('/api/v1/account/user/bulk/resend-activation-links', {
          user_ids: selectedUsers.map(user => user.id),
        })
        dispatch(
          showSnackbar({
            message: t`Activation Link has been sent!`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      } catch (error) {
        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message ||
              t`Something went wrong trying to send activation links`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      }
    }
  }

  const addItem = () => {
    setVisible(true)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/user/bulk/delete', {
        user_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredUsers = users.filter(u => !payload[u.id])
        initializeUsers(filteredUsers)
        setSelected({})
        dispatch(
          showSnackbar({
            message: t`User deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            message: err.response?.data?.message
              ? err.response.data.message
              : t`User failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these users`,
      description: t`This action can't be undone.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  /**
   * Handle Table Row click.
   * @param {number} rowIndex
   * @param {*} row object that represents a row.
   * @param {boolean} [isCheckbox=false]
   * @returns
   */
  function handleRowClick(rowIndex, row, isCheckbox = false) {
    if (shiftKey) {
      handleSelectMultipleRows(lastClickedRowIdx, rowIndex)
    } else {
      numSelected || isCheckbox
        ? setSelected({
            ...selected,
            [row.id]: !selected[row.id],
          })
        : setSelectedUser(row.id)
    }
    setLastClickedRowIdx(rowIndex)
  }

  const userName = (selectedUser && byId[selectedUser]?.name) || 'New User'

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
      data-test-page-id="admin-users"
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Users`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Control who has access to what within your Сlaravine organization`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div
            className={classes.table}
            style={{ height: 'calc(100% - 145px)' }}
            data-test-cmp-id="admin-users-container"
          >
            <EnhancedTableToolbar
              numSelected={numSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              term={searchTerm}
              deleteItems={deleteItems}
              noAdd={false}
              onReset={onReset}
              blockMultipleEdit={false}
            >
              {(isAdmin || isManagerWithPermission) && (
                <Tooltip title={resendTooltipText}>
                  <Fab
                    style={{
                      color: 'white',
                      marginRight: '10px',
                      backgroundColor: isResendEnable ? colors.purple.main : colors.gray.main,
                    }}
                    size="small"
                    aria-label={resendTooltipText}
                    className={classes.fab}
                    disableRipple={!isResendEnable}
                    onClick={onResend}
                  >
                    <Send />
                  </Fab>
                </Tooltip>
              )}
              <Tooltip title={t`Reset password`}>
                <Fab
                  style={{
                    color: 'white',
                    marginRight: '10px',
                  }}
                  color="secondary"
                  size="small"
                  aria-label="Reset Password"
                  className={classes.fab}
                  onClick={onReset}
                >
                  <Security />
                </Fab>
              </Tooltip>
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div className={classes.table}>
              <CvsTable
                columns={columns}
                rows={displayUsers}
                onSortedRowsChange={sorted => setSortedUsers(sorted)}
                orderBy="name"
                sortOrder="asc"
                keyField="id"
                noAdd={false}
                isLoading={isPending}
                addItem={addItem}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                multiSelect={true}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <PersonOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Users Found`,
                  description: t`Try a different search or add a new user.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayUsers.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row, index }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        handleRowClick(index, row)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            handleRowClick(index, row, true)
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          {getCellContent('avatar', row)}
                          {row.name}
                        </span>
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.email}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.type}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.last_login)}
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
      <CvsDrawer
        data-test-cmp-id="drawer-edit-user"
        visible={Boolean(visible)}
        onClose={onClose}
        title={t`Edit ${userName}`}
      >
        <EditUser userId={selectedUser} onClose={onClose} />
      </CvsDrawer>
    </motion.div>
  )
}
